import { MIN_PANEL_DIMENSION_MM } from "@plaatplan/core";
import { useMemo, useState } from "react";
import { useLocale } from "../i18n/useLocale";
import { parsePartLine } from "./parsePartLine";

export function SubprojectPartInput({ projectName, onAddPart, onAddEmptyPanel }) {
  const { display } = useLocale();
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const feedbackId = `part-feedback-${projectName.replace(/\s+/g, "-").toLowerCase()}`;
  const minLength = display.formatLength(MIN_PANEL_DIMENSION_MM);

  const parsed = useMemo(
    () => parsePartLine(value, { unitSystem: display.unitSystem }),
    [value, display.unitSystem],
  );
  const preview = parsed.ok
    ? display.t("web.parsePartLine.preview", {
      size: display.formatPanelSize(parsed.part.length, parsed.part.width),
      quantity: parsed.part.quantity,
      nameSuffix: parsed.part.name ? ` · ${parsed.part.name}` : "",
    })
    : null;

  const resolveError = (errorKey) => {
    if (errorKey === "web.parsePartLine.lengthMin" || errorKey === "web.parsePartLine.widthMin") {
      return display.t(errorKey, { min: minLength });
    }
    if (errorKey === "web.parsePartLine.empty") {
      return display.t(display.unitSystem === "imperial"
        ? "web.parsePartLine.emptyImperial"
        : "web.parsePartLine.empty");
    }
    if (errorKey === "web.parsePartLine.invalidFormat") {
      return display.t(display.unitSystem === "imperial"
        ? "web.parsePartLine.invalidFormatImperial"
        : "web.parsePartLine.invalidFormat");
    }
    return display.t(errorKey);
  };

  const submit = () => {
    const result = parsePartLine(value, { unitSystem: display.unitSystem });
    if (!result.ok) {
      setError(resolveError(result.errorKey));
      return;
    }

    onAddPart(result.part);
    setValue("");
    setError("");
  };

  return (
    <div
      className="subproject-part-input"
      aria-label={display.t("web.parsePartLine.inputAria", { project: projectName })}
    >
      <div className="subproject-part-row">
        <input
          className="subproject-part-field"
          type="text"
          value={value}
          placeholder={display.t(display.unitSystem === "imperial"
            ? "web.parsePartLine.placeholderImperial"
            : "web.parsePartLine.placeholder")}
          onChange={(event) => {
            setValue(event.target.value);
            if (error) setError("");
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              submit();
            }
          }}
          aria-invalid={Boolean(error)}
          aria-describedby={preview || error ? feedbackId : undefined}
        />
        <button type="button" className="secondary-button" onClick={submit}>
          {display.t("web.parsePartLine.addPanel")}
        </button>
      </div>
      <div className="subproject-part-feedback" id={feedbackId} role="status">
        {error && <span className="quick-part-error">{error}</span>}
        {!error && preview && (
          <span className="quick-part-preview">
            {display.t("web.parsePartLine.understood")} <strong>{preview}</strong>
          </span>
        )}
      </div>
      <button type="button" className="text-button add-panel-button" onClick={onAddEmptyPanel}>
        <strong>+</strong> {display.t("web.parsePartLine.addEmpty")}
      </button>
    </div>
  );
}
